<template>
    <div class="chrome">
        <div class="chrome-tabs" @activeTabChange="activeTabChange" @tabAdd="tabAdd" @tabRemove="tabRemove">
            <div class="chrome-tabs-content">
                <div v-for="(tab, index) in tabs" :class="{ 'chrome-tab': true, 'chrome-tab-current': tab.isActive }" :hash="tab.hash">
                    <div class="chrome-tab-background">
                        <svg version="1.1" xmlns="http://www.w3.org/2000/svg">
                            <defs>
                                <symbol id="topleft" viewBox="0 0 214 29" ><path d="M14.3 0.1L214 0.1 214 29 0 29C0 29 12.2 2.6 13.2 1.1 14.3-0.4 14.3 0.1 14.3 0.1Z"/></symbol>
                                <symbol id="topright" viewBox="0 0 214 29">
                                    <use xlink:href="#topleft"/>
                                </symbol>
                                <clipPath id="crop">
                                    <rect class="mask" width="100%" height="100%" x="0"/>
                                </clipPath>
                            </defs>
                            <svg width="50%" height="100%" transfrom="scale(-1, 1)">
                                <use xlink:href="#topleft" width="214" height="29" class="chrome-tab-background"/>
                                <use xlink:href="#topleft" width="214" height="29" class="chrome-tab-shadow"/>
                            </svg>
                            <g transform="scale(-1, 1)">
                                <svg width="50%" height="100%" x="-100%" y="0">
                                    <use xlink:href="#topright" width="214" height="29" class="chrome-tab-background"/>
                                    <use xlink:href="#topright" width="214" height="29" class="chrome-tab-shadow"/>
                                </svg>
                            </g>
                        </svg>
                    </div>

                    <div class="chrome-tab-favicon" :style="{ 'background-image': `url(${tab.image})` }"></div>
                    <div class="chrome-tab-title" v-html="tab.header"></div>
                    <div v-show="close && tab.close" class="chrome-tab-close"></div>
                </div>
            </div>
            <div class="chrome-tabs-bottom-bar"></div>
        </div>

        <div class="chrome-tabs-content">
            <slot/>
        </div>
    </div>
</template>

<script>
    import Draggabilly from 'draggabilly'

    // for adding tab, use
    /*
        {ChromeTabsInstace}.addTab({
          title: 'New Tab',
          favicon: 'demo/images/default-favicon.png'
        })
    */

    // for removing tab, use
    /*
        {ChromeTabsInstace}.removeTab(el.querySelector('.chrome-tab-current'))
    */

    const tabTemplate = `
      <div class="chrome-tab">
        <div class="chrome-tab-background">
          <svg version="1.1" xmlns="http://www.w3.org/2000/svg"><defs><symbol id="topleft" viewBox="0 0 214 29" ><path d="M14.3 0.1L214 0.1 214 29 0 29C0 29 12.2 2.6 13.2 1.1 14.3-0.4 14.3 0.1 14.3 0.1Z"/></symbol><symbol id="topright" viewBox="0 0 214 29"><use xlink:href="#topleft"/></symbol><clipPath id="crop"><rect class="mask" width="100%" height="100%" x="0"/></clipPath></defs><svg width="50%" height="100%" transfrom="scale(-1, 1)"><use xlink:href="#topleft" width="214" height="29" class="chrome-tab-background"/><use xlink:href="#topleft" width="214" height="29" class="chrome-tab-shadow"/></svg><g transform="scale(-1, 1)"><svg width="50%" height="100%" x="-100%" y="0"><use xlink:href="#topright" width="214" height="29" class="chrome-tab-background"/><use xlink:href="#topright" width="214" height="29" class="chrome-tab-shadow"/></svg></g></svg>
        </div>
        <div class="chrome-tab-favicon"></div>
        <div class="chrome-tab-title"></div>
        <div class="chrome-tab-close"></div>
      </div>`

    const defaultTapProperties = {
        title: '',
        favicon: ''
    }

    let instanceId = 0

    class ChromeTabs {
        constructor() {
            this.draggabillyInstances = []
        }

        init(el, options) {
            this.el = el
            this.options = options

            this.instanceId = instanceId
            this.el.setAttribute('data-chrome-tabs-instance-id', this.instanceId)
            instanceId += 1

            this.setupStyleEl()
            this.setupEvents()
            this.layoutTabs()
            this.fixZIndexes()
            this.setupDraggabilly()
        }

        emit(eventName, data) {
            this.el.dispatchEvent(new CustomEvent(eventName, { detail: data }))
        }

        setupStyleEl() {
            this.animationStyleEl = document.createElement('style')
            this.el.appendChild(this.animationStyleEl)
        }

        setupEvents() {
            window.addEventListener('resize', event => this.layoutTabs())

            this.el.addEventListener('dblclick', event => this.addTab())

            this.el.addEventListener('click', ({target}) => {
                if (target.classList.contains('chrome-tab')) {
                    this.setCurrentTab(target)
                } else if (target.classList.contains('chrome-tab-close')) {
                    this.removeTab(target.parentNode)
                } else if (target.classList.contains('chrome-tab-title') || target.classList.contains('chrome-tab-favicon')) {
                    this.setCurrentTab(target.parentNode)
                }
            })
        }

        get tabEls() {
            return Array.prototype.slice.call(this.el.querySelectorAll('.chrome-tab'))
        }

        get tabContentEl() {
            return this.el.querySelector('.chrome-tabs-content')
        }

        get tabWidth() {
            const tabsContentWidth = this.tabContentEl.clientWidth - this.options.tabOverlapDistance
            const width = (tabsContentWidth / this.tabEls.length) + this.options.tabOverlapDistance
            return Math.max(this.options.minWidth, Math.min(this.options.maxWidth, width))
        }

        get tabEffectiveWidth() {
            return this.tabWidth - this.options.tabOverlapDistance
        }

        get tabPositions() {
            const tabEffectiveWidth = this.tabEffectiveWidth
            let left = 0
            let positions = []

            this.tabEls.forEach((tabEl, i) => {
                positions.push(left)
                left += tabEffectiveWidth
            })
            return positions
        }

        layoutTabs() {
            const tabWidth = this.tabWidth

            this.cleanUpPreviouslyDraggedTabs()
            this.tabEls.forEach((tabEl) => tabEl.style.width = tabWidth + 'px')
            requestAnimationFrame(() => {
                let styleHTML = ''
                this.tabPositions.forEach((left, i) => {
                    styleHTML += `
              .chrome-tabs[data-chrome-tabs-instance-id="${ this.instanceId }"] .chrome-tab:nth-child(${ i + 1 }) {
              transform: translate3d(${ left }px, 0, 0)
            }`
                })
                this.animationStyleEl.innerHTML = styleHTML
            })
        }

        fixZIndexes() {
            const bottomBarEl = this.el.querySelector('.chrome-tabs-bottom-bar')
            const tabEls = this.tabEls

            tabEls.forEach((tabEl, i) => {
                let zIndex = tabEls.length - i

                if (tabEl.classList.contains('chrome-tab-current')) {
                    bottomBarEl.style.zIndex = tabEls.length + 1
                    zIndex = tabEls.length + 2
                }
                tabEl.style.zIndex = zIndex
            })
        }

        createNewTabEl() {
            const div = document.createElement('div')
            div.innerHTML = tabTemplate
            return div.firstElementChild
        }

        addTab(tabProperties) {
            const tabEl = this.createNewTabEl()

            tabEl.classList.add('chrome-tab-just-added')
            setTimeout(() => tabEl.classList.remove('chrome-tab-just-added'), 500)

            tabProperties = Object.assign({}, defaultTapProperties, tabProperties)
            this.tabContentEl.appendChild(tabEl)
            this.updateTab(tabEl, tabProperties)
            this.emit('tabAdd', { tabEl })
            this.setCurrentTab(tabEl)
            this.layoutTabs()
            this.fixZIndexes()
            this.setupDraggabilly()
        }

        setCurrentTab(tabEl) {
            const currentTab = this.el.querySelector('.chrome-tab-current')
            if (currentTab) {
                currentTab.classList.remove('chrome-tab-current')
            }
            tabEl.classList.add('chrome-tab-current')
            this.fixZIndexes()
            this.emit('activeTabChange', { tabEl })
        }

        removeTab(tabEl) {
            if (tabEl.classList.contains('chrome-tab-current')) {
                if (tabEl.previousElementSibling) {
                    this.setCurrentTab(tabEl.previousElementSibling)
                } else if (tabEl.nextElementSibling) {
                    this.setCurrentTab(tabEl.nextElementSibling)
                }
            }
            tabEl.parentNode.removeChild(tabEl)
            this.emit('tabRemove', { tabEl })
            this.layoutTabs()
            this.fixZIndexes()
            this.setupDraggabilly()
        }

        updateTab(tabEl, tabProperties) {
            tabEl.querySelector('.chrome-tab-title').textContent = tabProperties.title
            tabEl.querySelector('.chrome-tab-favicon').style.backgroundImage = `url('${tabProperties.favicon}')`
        }

        cleanUpPreviouslyDraggedTabs() {
            this.tabEls.forEach((tabEl) => tabEl.classList.remove('chrome-tab-just-dragged'))
        }

        setupDraggabilly() {
            const tabEls = this.tabEls
            const tabEffectiveWidth = this.tabEffectiveWidth
            const tabPositions = this.tabPositions

            this.draggabillyInstances.forEach(draggabillyInstance => draggabillyInstance.destroy())

            tabEls.forEach((tabEl, originalIndex) => {
                const originalTabPositionX = tabPositions[originalIndex]
                const draggabillyInstance = new Draggabilly(tabEl, {
                    axis: 'x',
                    containment: this.tabContentEl
                })

                this.draggabillyInstances.push(draggabillyInstance)

                draggabillyInstance.on('dragStart', () => {
                    this.cleanUpPreviouslyDraggedTabs()
                    tabEl.classList.add('chrome-tab-currently-dragged')
                    this.el.classList.add('chrome-tabs-sorting')
                    this.fixZIndexes()
                })

                draggabillyInstance.on('dragEnd', () => {
                    const finalTranslateX = parseFloat(tabEl.style.left, 10)
                    tabEl.style.transform = `translate3d(0, 0, 0)`

                    // Animate dragged tab back into its place
                    requestAnimationFrame(() => {
                        tabEl.style.left = '0'
                        tabEl.style.transform = `translate3d(${ finalTranslateX }px, 0, 0)`

                        requestAnimationFrame(() => {
                            tabEl.classList.remove('chrome-tab-currently-dragged')
                            this.el.classList.remove('chrome-tabs-sorting')

                            this.setCurrentTab(tabEl)
                            tabEl.classList.add('chrome-tab-just-dragged')

                            requestAnimationFrame(() => {
                                tabEl.style.transform = ''

                                this.setupDraggabilly()
                            })
                        })
                    })
                })

                draggabillyInstance.on('dragMove', (event, pointer, moveVector) => {
                    // Current index be computed within the event since it can change during the dragMove
                    const tabEls = this.tabEls
                    const currentIndex = tabEls.indexOf(tabEl)

                    const currentTabPositionX = originalTabPositionX + moveVector.x
                    const destinationIndex = Math.max(0, Math.min(tabEls.length, Math.floor((currentTabPositionX + (tabEffectiveWidth / 2)) / tabEffectiveWidth)))

                    if (currentIndex !== destinationIndex) {
                        this.animateTabMove(tabEl, currentIndex, destinationIndex)
                    }
                })
            })
        }

        animateTabMove(tabEl, originIndex, destinationIndex) {
            if (destinationIndex < originIndex) {
                tabEl.parentNode.insertBefore(tabEl, this.tabEls[destinationIndex])
            } else {
                tabEl.parentNode.insertBefore(tabEl, this.tabEls[destinationIndex + 1])
            }
        }
    }

    export default {
        name: "chrome-tabs",
        props: {
            close: {
                type: Boolean,
                default: true
            },
            'first-tab': {
                default: 0
            }
        },
        data: () => {
            return {
                chrome: new ChromeTabs(),
                chrometabsoptions: {
                    tabOverlapDistance: 14,
                    minWidth: 45,
                    maxWidth: 230
                },
                tabs: []
            }
        },
        created() {
            this.tabs = this.$children
        },
        mounted() {
            let element = document.querySelector('.chrome-tabs')
            this.chrome.init(element, this.chrometabsoptions)

            if (this.tabs[0]) {
                this.tabs[0].isActive = true
            }
        },
        methods: {
            activeTabChange: function({ detail }) {
                this.tabs.forEach(tab => {
                    tab.isActive = (tab.hash === detail.tabEl.getAttribute('hash'))
                })

                this.$emit('change', detail)
            },
            tabAdd: function({ detail }) {
                this.$emit('tabAdded', detail)
            },
            tabRemove: function({ detail }) {
                this.tabs = this.tabs.filter(tab => tab.hash === '#' + detail.tabEl.getAttribute('hash'))
                this.$emit('tabRemoved', detail)
            },
        },
    }

</script>

<style>
    .chrome-tabs {
        box-sizing: border-box;
        position: relative;
        font-size: 10px;
        height: 4.2em;
        background: linear-gradient(#dad9da, #d9d8d9);
        padding: 1em 1.2em 0.45em 1.2em;
        border-radius: 0.5em 0.5em 0 0;
        box-shadow: 0 0.05em #b7b7b7;
        overflow: hidden;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
    }
    .chrome-tabs * {
        box-sizing: inherit;
        font-family: inherit;
    }
    .chrome-tabs .chrome-tabs-bottom-bar {
        position: absolute;
        bottom: 0;
        height: 0.45em;
        left: 0;
        width: 100%;
        background: #f2f2f2;
        box-shadow: 0 -0.05em rgba(0,0,0,0.27);
        z-index: 20;
    }
    .chrome-tabs .chrome-tabs-content {
        position: relative;
        width: 100%;
        height: 100%;
        overflow: hidden;
    }
    .chrome-tabs .chrome-tab {
        position: absolute;
        left: 0;
        height: 2.8em;
        width: 24em;
        border: 0;
        margin: 0;
        z-index: 1;
    }
    .chrome-tabs .chrome-tab,
    .chrome-tabs .chrome-tab * {
        user-select: none;
        cursor: default;
    }
    .chrome-tabs .chrome-tab .chrome-tab-background {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        overflow: hidden;
        pointer-events: none;
    }
    .chrome-tabs .chrome-tab .chrome-tab-background > svg {
        width: 100%;
        height: 100%;
    }
    .chrome-tabs .chrome-tab .chrome-tab-background > svg .chrome-tab-shadow {
        fill: none;
        stroke: rgba(0,0,0,0.27);
        stroke-width: 0.5px;
    }
    .chrome-tabs .chrome-tab .chrome-tab-background > svg .chrome-tab-background {
        fill: #d0d0d0;
        transform: translateX(0.25px) translateY(0.25px);
    }
    .chrome-tabs .chrome-tab.chrome-tab-current {
        z-index: 999;
    }
    .chrome-tabs .chrome-tab.chrome-tab-current .chrome-tab-background > svg .chrome-tab-background {
        fill: #f2f2f2;
    }
    .chrome-tabs .chrome-tab.chrome-tab-just-added {
        top: 10px;
        animation: chrome-tab-just-added 120ms forwards ease-in-out;
    }
    @-moz-keyframes chrome-tab-just-added {
        to {
            top: 0;
        }
    }
    @-webkit-keyframes chrome-tab-just-added {
        to {
            top: 0;
        }
    }
    @-o-keyframes chrome-tab-just-added {
        to {
            top: 0;
        }
    }
    @keyframes chrome-tab-just-added {
        to {
            top: 0;
        }
    }
    .chrome-tabs.chrome-tabs-sorting .chrome-tab:not(.chrome-tab-currently-dragged),
    .chrome-tabs:not(.chrome-tabs-sorting) .chrome-tab.chrome-tab-just-dragged {
        transition: transform 120ms ease-in-out;
    }
    .chrome-tabs .chrome-tab-favicon {
        position: relative;
        margin-left: 1.6em;
        height: 1.6em;
        width: 1.6em;
        background-size: 1.6em;
        margin-top: 0.6em;
        z-index: 3;
        display: inline-block;
        vertical-align: top;
        pointer-events: none;
    }
    .chrome-tabs .chrome-tab-title {
        position: relative;
        display: inline-block;
        vertical-align: top;
        color: #222;
        padding: 0 0.25em;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 1.2em;
        margin-top: 0.5em;
        max-width: calc(100% - 5em);
        pointer-events: none;
    }
    .chrome-tabs .chrome-tab-close {
        position: absolute;
        width: 1.4em;
        height: 1.4em;
        border-radius: 50%;
        z-index: 2;
        right: 1.4em;
        top: 0.7em;
    }
    .chrome-tabs .chrome-tab-close::before {
        content: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 14'><path stroke='#5a5a5a' stroke-linecap='round' stroke-width='1.125' d='M4 4 L10 10 M10 4 L4 10'></path></svg>");
        position: absolute;
        display: block;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
    }
    .chrome-tabs .chrome-tab-close:hover::before,
    .chrome-tabs .chrome-tab-close:hover:active::before {
        content: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 14'><path stroke='#fff' stroke-linecap='round' stroke-width='1.2' d='M4 4 L10 10 M10 4 L4 10'></path></svg>");
    }
    .chrome-tabs .chrome-tab-close:hover {
        background: #e25c4b;
    }
    .chrome-tabs .chrome-tab-close:hover:active {
        background: #b74a3b;
    }


    .chrome-tabs .chrome-tab {
        width: 243px !important;
    }

    .chrome-tabs .chrome-tab:nth-child(1) {
        transform: translate3d(0px, 0, 0) !important;
    }

    .chrome-tabs .chrome-tab:nth-child(2) {
        transform: translate3d(229px, 0, 0) !important;
    }
</style>



// WEBPACK FOOTER //
// resources/assets/js/components/ChromeTabs/ChromeTabs.vue